import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { NgbDateStruct, NgbCalendar, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { faCalendar, faTimes } from '@fortawesome/free-solid-svg-icons';
import { DateTime } from 'luxon';

export interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

@Component({
  selector: 'app-table-date-range-picker',
  templateUrl: './table-date-range-picker.html',
  styleUrls: ['./table-date-range-picker.scss']
})
export class TableDateRangePicker {

  @Input() startDate: string | null = null;
  @Input() endDate: string | null = null;
  @Input() maxDate: string | null = DateTime.now().toISODate();
  @Output() startDateChange = new EventEmitter<string | null>();
  @Output() endDateChange = new EventEmitter<string | null>();

  isOpen: boolean = false;
  hoveredDate: NgbDateStruct | null = null;

  fromModel: NgbDateStruct | null = null;
  toModel: NgbDateStruct | null = null;

  faCalendar = faCalendar;
  faTimes = faTimes;

  @ViewChild('dp') dp!: NgbDatepicker;

  positions: ConnectedPosition[] = [
  {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
    offsetY: 4
  }
];

  constructor(
    private calendar: NgbCalendar,
    private cdr: ChangeDetectorRef) {
  }

  toggle() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.fromModel = this.startDate ? this.toNgbDate(this.startDate) : null;
      this.toModel = this.endDate ? this.toNgbDate(this.endDate) : null;

      if (this.fromModel) {
        setTimeout(() => {
          this.dp?.navigateTo({ year: this.fromModel!.year, month: this.fromModel!.month });
        });
      }
    }
  }

  toNgbDate(dateStr: string): NgbDateStruct {
    const d = DateTime.fromISO(dateStr);
    return { year: d.year, month: d.month, day: d.day };
  }

  toIsoString(date: NgbDateStruct): string {
    return DateTime.fromObject({ year: date.year, month: date.month, day: date.day }).toISODate()!;
  }

  get displayText(): string {
    if (this.startDate && this.endDate) {
      return `${this.formatDisplay(this.startDate)} - ${this.formatDisplay(this.endDate)}`;
    }
    return '-';
  }

  formatDisplay(dateStr: string): string {
    return DateTime.fromISO(dateStr).toFormat('dd-MM-yyyy');
  }

  onDateSelect(date: NgbDateStruct) {
    if (!this.fromModel || (this.fromModel && this.toModel)) {
      this.fromModel = date;
      this.toModel = null;
    } else if (!this.toModel && this.afterOrEqual(date, this.fromModel)) {
      this.toModel = date;
    } else {
      this.fromModel = date;
      this.toModel = null;
    }

    this.cdr.detectChanges();
  }

  afterOrEqual(a: NgbDateStruct, b: NgbDateStruct): boolean {
    const da = new Date(a.year, a.month - 1, a.day).getTime();
    const db = new Date(b.year, b.month - 1, b.day).getTime();
    return da >= db;
  }

  isInside(date: NgbDateStruct): boolean {
    if (!this.fromModel) return false;
    const to = this.toModel || this.hoveredDate;
    if (!to) return false;
    const dDate = new Date(date.year, date.month - 1, date.day).getTime();
    const dFrom = new Date(this.fromModel.year, this.fromModel.month - 1, this.fromModel.day).getTime();
    const dTo = new Date(to.year, to.month - 1, to.day).getTime();
    return dDate > Math.min(dFrom, dTo) && dDate < Math.max(dFrom, dTo);
  }

  isRange(date: NgbDateStruct): boolean {
    const isFrom = this.fromModel && this.equals(date, this.fromModel);
    const isTo = this.toModel && this.equals(date, this.toModel);
    const result = !!isFrom || !!isTo || this.isInside(date);
    return result;
  }

  equals(a: NgbDateStruct, b: NgbDateStruct): boolean {
    return a.year === b.year && a.month === b.month && a.day === b.day;
  }

  apply() {
    if (this.fromModel && !this.toModel) {
      this.toModel = this.fromModel;
    }

    if (this.fromModel) {
      const startStr = this.toIsoString(this.fromModel);
      this.startDate = startStr;
      this.startDateChange.emit(startStr);
    }
    if (this.toModel) {
      const endStr = this.toIsoString(this.toModel);
      this.endDate = endStr;
      this.endDateChange.emit(endStr);
    }
    this.isOpen = false;
  }

  clear() {
    this.fromModel = null;
    this.toModel = null;
    this.startDate = null;
    this.endDate = null;
    this.startDateChange.emit(null);
    this.endDateChange.emit(null);
    this.isOpen = false;
  }

  cancel() {
    this.isOpen = false;
  }

  setPreset(preset: 'today' | 'yesterday' | 'thisMonth' | 'lastMonth' | 'thisYear') {
    const now = DateTime.now();
    let from: DateTime;
    let to: DateTime;

    switch (preset) {
      case 'today':
        from = now;
        to = now;
        break;
      case 'yesterday':
        from = now.minus({ days: 1 });
        to = now.minus({ days: 1 });
        break;
      case 'thisMonth':
        from = now.startOf('month');
        to = now.endOf('month');
        break;
      case 'lastMonth':
        from = now.minus({ months: 1 }).startOf('month');
        to = now.minus({ months: 1 }).endOf('month');
        break;
      case 'thisYear':
        from = now.startOf('year');
        to = now.endOf('year');
        break;
    }

    this.fromModel = { year: from.year, month: from.month, day: from.day };
    this.toModel = { year: to.year, month: to.month, day: to.day };

    this.apply();
  }

  get ngbMaxDate(): NgbDateStruct {
    if (this.maxDate) {
      return this.toNgbDate(this.maxDate);
    }
    const future = DateTime.now().plus({ years: 10 });
    return { year: future.year, month: future.month, day: future.day };
  }
}